import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'

export default function ConfigPage() {
  const [wp_base_url, setWpBaseUrl] = useState('')
  const [wp_app_user, setWpAppUser] = useState('')
  const [wp_app_password, setWpAppPassword] = useState('')
  const [openai_api_key, setOpenaiApiKey] = useState('')
  const [wp_subject_owner_user_id, setWpOwner] = useState<number | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [testMessage, setTestMessage] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)
  const [users, setUsers] = useState<Array<{id:number,email:string,is_superuser:boolean}>>([])
  const [me, setMe] = useState<{id:number,email:string,is_superuser:boolean} | null>(null)

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings/me')
        setWpBaseUrl(data.wp_base_url || '')
        setWpAppUser(data.wp_app_user || '')
        setWpAppPassword(data.wp_app_password || '')
        setOpenaiApiKey(data.openai_api_key || '')
        if (data.wp_subject_owner_user_id != null) setWpOwner(Number(data.wp_subject_owner_user_id))
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Erreur chargement paramètres')
      }
      // Charger profil courant
      try {
        const { data } = await api.get('/fapi/users/me')
        setMe(data)
        // Tenter la liste complète si admin
        if (data?.is_superuser) {
          const res = await api.get('/fapi/users/admin/all')
          setUsers(res.data)
        } else {
          setUsers([data])
        }
      } catch {}
    })()
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setError(null)
    setTestMessage(null)
    setTestError(null)
    try {
      await api.put('/settings/me', { wp_base_url, wp_app_user, wp_app_password, openai_api_key, wp_subject_owner_user_id: wp_subject_owner_user_id ?? (me?.id ?? null) })
      setMessage('Paramètres sauvegardés.')
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur sauvegarde paramètres')
    }
  }

  const testCourses = async () => {
    setTestMessage(null)
    setTestError(null)
    try {
      const { data } = await api.get('/wp/courses')
      setTestMessage(`Formations détectées: ${data.length}`)
    } catch (e: any) {
      setTestError(e?.response?.data?.detail || 'Erreur récupération formations')
    }
  }

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-6">
        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => scrollToSection('wordpress')}
            className="group relative overflow-hidden rounded-2xl border border-blue-200/70 bg-gradient-to-br from-blue-50/80 to-white/80 p-6 text-left shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 dark:border-blue-900/50 dark:from-blue-950/40 dark:to-zinc-950/80"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">🌐</span>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">WordPress</h3>
            </div>
            <p className="text-sm text-blue-700/80 dark:text-blue-300/80">Configuration LearnDash</p>
            <div className="absolute bottom-2 right-2 text-blue-400 dark:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
              ↓
            </div>
          </button>

          <button
            onClick={() => scrollToSection('openai')}
            className="group relative overflow-hidden rounded-2xl border border-purple-200/70 bg-gradient-to-br from-purple-50/80 to-white/80 p-6 text-left shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 dark:border-purple-900/50 dark:from-purple-950/40 dark:to-zinc-950/80"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">🤖</span>
              <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100">OpenAI</h3>
            </div>
            <p className="text-sm text-purple-700/80 dark:text-purple-300/80">Clé API OpenAI</p>
            <div className="absolute bottom-2 right-2 text-purple-400 dark:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
              ↓
            </div>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {/* WordPress Section */}
          <section id="wordpress" className="scroll-mt-6 space-y-6 rounded-3xl border border-blue-100/70 bg-white/90 p-8 shadow-xl shadow-blue-500/10 dark:border-blue-900/50 dark:bg-zinc-950/90">
            <div className="flex items-center gap-3 pb-4 border-b border-blue-200/50 dark:border-blue-800/50">
              <span className="text-3xl">🌐</span>
              <div>
                <h2 className="text-xl font-semibold text-blue-900 dark:text-blue-100">WordPress & LearnDash</h2>
                <p className="text-sm text-blue-700/70 dark:text-blue-300/70">Connexion à votre site WordPress avec LearnDash</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">WordPress Base URL</label>
                <input 
                  className="w-full border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" 
                  value={wp_base_url} 
                  onChange={e=>setWpBaseUrl(e.target.value)} 
                  placeholder="https://votre-site-wp.com" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">WP Application User</label>
                <input 
                  className="w-full border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" 
                  value={wp_app_user} 
                  onChange={e=>setWpAppUser(e.target.value)} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">WP Application Password</label>
                <input 
                  type="password"
                  className="w-full border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" 
                  value={wp_app_password} 
                  onChange={e=>setWpAppPassword(e.target.value)} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">Propriétaire des sujets WordPress</label>
                <select
                  className="w-full border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={wp_subject_owner_user_id ?? me?.id ?? ''}
                  onChange={(e) => setWpOwner(Number(e.target.value))}
                >
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.email}{u.is_superuser ? ' (admin)' : ''}</option>
                  ))}
                </select>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">Les formations LearnDash seront automatiquement importées comme sujets appartenant à cet utilisateur.</p>
              </div>
              <div className="pt-4 border-t border-blue-200/50 dark:border-blue-800/50">
                <button 
                  className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300 dark:hover:bg-blue-950/70 transition" 
                  type="button" 
                  onClick={testCourses}
                >
                  🧪 Tester la connexion LearnDash
                </button>
                {testError && (
                  <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">
                    {testError}
                  </div>
                )}
                {testMessage && (
                  <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900/60 dark:text-emerald-200">
                    {testMessage}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* OpenAI Section */}
          <section id="openai" className="scroll-mt-6 space-y-6 rounded-3xl border border-purple-100/70 bg-white/90 p-8 shadow-xl shadow-purple-500/10 dark:border-purple-900/50 dark:bg-zinc-950/90">
            <div className="flex items-center gap-3 pb-4 border-b border-purple-200/50 dark:border-purple-800/50">
              <span className="text-3xl">🤖</span>
              <div>
                <h2 className="text-xl font-semibold text-purple-900 dark:text-purple-100">OpenAI</h2>
                <p className="text-sm text-purple-700/70 dark:text-purple-300/70">Configuration de votre clé API OpenAI (optionnel)</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">OpenAI API Key</label>
              <input 
                type="password" 
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition" 
                value={openai_api_key} 
                onChange={e=>setOpenaiApiKey(e.target.value)} 
                placeholder="sk-..." 
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                Permet d'utiliser les modèles GPT d'OpenAI pour vos agents.
              </p>
            </div>
          </section>

          {/* Messages & Submit */}
          <section className="rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">
                {error}
              </div>
            )}
            {message && (
              <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900/60 dark:text-emerald-200">
                {message}
              </div>
            )}
            <button 
              className="w-full rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600" 
              type="submit"
            >
              💾 Sauvegarder toutes les configurations
            </button>
          </section>
        </form>
      </div>
    </div>
  )
}
